import _ from 'lodash'

export const rscStore = {
  namespaced: true,
  state: {
    /**
     * The selected country/region/subregion from the country dropdown
     * @type {Array}
     */
    selected: [],

    /**
     * Regime lookup object
     * @type {Object}
     */
    dem: [
      {
        dem: 1,
        name: 'Authoritarian regime'
      },
      {
        dem: 2,
        name: 'Hybrid regime'
      },
      {
        dem: 3,
        name: 'Democracy'
      }
    ],

    /**
     * Democracy performance lookip
     */
    demperf: [
      {
        demperf: 1,
        name: 'Weak/Low performance'
      },
      {
        demperf: 2,
        name: 'Mid-range performance'
      },
      {
        demperf: 3,
        name: 'High performance'
      }
    ],

    /**
     * All countries/regions/subregions
     * @type {Object}
     */
    all: {},

    /**
     * Some regions do not have subregions.
     * This is a list of ids
     * @type {Array}
     */
    regionsWithoutSubregions: [993, 995, 6000, 999999],

    /**
     * List of regions/subregion/countries that should only be visible on the
     * Covid19 profiles
     * @type {Array}
     */
    rscCovidProfileOnly: [6000, 6004, 6005, 6006],

    /**
     * List of countries that should only be visible on the
     * Covid19 profiles
     * @type {Array}
     */
    countriesCovidProfileOnly: [6004, 6005, 6006],

    /**
     * Regions that are not selectable for comparison
     * @type {Array}
     */
    notSelectableRegions: [999999]
  },

  getters: {

    /**
     * Checks if a r/s/c is selected
     * @param  {obj}  state The Vuex state
     * @param  {object} obj   The selected country object
     * @return {Boolean}
     */
    isSelected: (state, getters) => (obj) => {
      let result = _.find(state.selected, (rsc) => {
        return rsc.id === obj.id
      })
      return !!result
    },

    /**
     * Returns the first selected
     * @param  {Object} state rscStore.state
     * @return {undefined|obj} The selected country object
     */
    firstSelected (state) {
      return state.selected[0]
    },

    /**
     * Return a r/s/c based on id
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {obj}  r/s/c object
     */
    getFromId: (state, getters) => (id) => {
      let result = false
      _.each(state.all.regions, (region, rI) => {
        if (parseInt(region.id) === parseInt(id)) result = region
        _.each(region.subregions, (subregion, srI) => {
          if (parseInt(subregion.id) === parseInt(id)) result = subregion
          _.each(subregion.countries, (country, cI) => {
            if (parseInt(country.id) === parseInt(id)) result = country
          })
        })
      })
      return result
    },

    /**
     * Return global object
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {obj}  r/s/c object
     */
    getGlobal: (state, getters) => {
      if (state.all.regions && state.all.regions[999]) {
        return state.all.regions[999]
      }
    },

    /**
     * Return the regime object by country data row
     */
    getRegimenameByDataRow: (state) => (row) => {
      let dem = parseInt(row.dem)
      let demperf = parseInt(row.demperf)

      if (!dem || !demperf) {
        return false
      }

      let demObj = _.find(state.dem, (d) => {
        return d.dem === dem
      })

      if (dem === 3) {
        let demPerfObj = _.find(state.demperf, (d) => {
          return d.demperf === demperf
        })

        return demPerfObj.name + ' ' + demObj.name
      }

      return demObj.name
    },

    /**
     * Return a regime based on id
     * @param  {obj}  state The Vuex state
     * @return {obj}  r/s/c object
     */
    getRegimeById: (state) => (id) => {
      let result

      if (id && id > 0) {
        _.each(state.dem, (regime) => {
          if (id && parseInt(regime.dem) === parseInt(id)) {
            result = regime
          }
        })
      }

      return result
    },

    /**
     * Return a regime performance based on id
     * @param  {obj}  state The Vuex state
     * @return {obj}  r/s/c object
     */
    getRegimePerformanceById: (state) => (id) => {
      let result

      if (id && id > 0) {
        _.each(state.demperf, (regime) => {
          if (id && parseInt(regime.demperf) === parseInt(id)) {
            result = regime
          }
        })
      }

      return result
    },

    /**
     * Return a country based on id
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {obj}  r/s/c object
     */
    getCountry: (state, getters) => (id) => {
      let result = false
      _.each(state.all.regions, (region, rI) => {
        _.each(region.subregions, (subregion, srI) => {
          _.each(subregion.countries, (country, cI) => {
            if (country.id === id) result = country
          })
        })
      })
      return result
    },

    /**
     * Return the subregion of a country
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {obj}  r/s/c object
     */
    getCountrySubregion: (state, getters) => (id) => {
      let result = false
      _.each(state.all.regions, (region, rI) => {
        _.each(region.subregions, (subregion, srI) => {
          _.each(subregion.countries, (country, cI) => {
            if (country.id === id) result = subregion
          })
        })
      })
      return result
    },

    /**
     * Return the region of a country
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {obj}  r/s/c object
     */
    getCountryRegion: (state, getters) => (id) => {
      let result = false
      _.each(state.all.regions, (region, rI) => {
        _.each(region.subregions, (subregion, srI) => {
          _.each(subregion.countries, (country, cI) => {
            if (country.id === id) result = region
          })
        })
      })
      return result
    },

    /**
     * Check if this is a region
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {Boolean}
     */
    isRegion: (state, getters) => (id) => {
      return !!(_.find(state.all.regions, ['id', id]))
    },

    /**
     * Check if this is a region
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {Boolean}
     */
    isSubregion: (state, getters) => (id) => {
      let result = false
      _.each(state.all.regions, (region, rI) => {
        _.each(region.subregions, (subregion, srI) => {
          if (subregion.id === id) result = true
        })
      })
      return result
    },

    /**
     * Check if this is a region
     * @param  {obj}  state The Vuex state
     * @param  {obj}  getters The Vuex getters
     * @return {Boolean}
     */
    isCountry: (state, getters) => (id) => {
      let result = false
      _.each(state.all.regions, (region, rI) => {
        _.each(region.subregions, (subregion, srI) => {
          _.each(subregion.countries, (country, cI) => {
            if (country.id === id) result = true
          })
        })
      })
      return result
    }
  },

  mutations: {
    /**
     * Add or remove a selected rsc
     * @param  {obj} state Vuex state
     * @param  {object} obj   The selected country object
     * @return {void}
     */
    select (state, obj) {
      state.selected.push(obj)
    },

    /**
     * Unselect a rsc
     * @param  {object} state Vuex state
     * @param  {object} obj   The selected country object
     * @return {void}
     */
    unselect (state, obj) {
      state.selected = _.reject(state.selected, (rsc) => {
        return rsc.id === obj.id
      })
    },

    /**
     * Resets the selected countries
     * @param  {[type]} state [description]
     * @return {[type]}       [description]
     */
    resetAllSelected (state) {
      state.selected = []
    }

  },

  actions: {
    /**
     * Select or unselect a country
     * @param  {obj} options.state     Vuex state
     * @param  {obj} options.commit    Vuex commit
     * @param  {obj} options.rootState Vuex rootState
     * @param  {obj} obj               The country select object
     * @return {void}
     */
    toggleSelect ({ state, commit, rootState }, obj) {
      let exists = !!_.find(state.selected, (rsc) => {
        return rsc.id === obj.id
      })
      if (exists) {
        commit('unselect', obj)
      } else {
        commit('select', obj)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/rscStore.js